import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';

import { SearchOutlined } from '@ant-design/icons';
import styled from 'styled-components';

import { getUserManagementAvailableCommunities } from 'apis/UserAPI';
import Dropdown from 'components/lib/Dropdown';
import Input from 'components/lib/Input';
import LoaderRibbed from 'components/lib/LoaderRibbed';
import { useDebounce } from 'utils/hooks/useDebounce';

import UserManagementSelectedCommunities, { UserManagementCommunityOption } from './UserManagementSelectedCommunities';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 50%;
  overflow: hidden;
  border-left: 1px solid var(--line-gray);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 16px;
  padding-left: 30px;
  padding-right: 20px;
`;

const Title = styled.span`
  font-size: 18px;
  font-family: var(--font-bold);
  color: var(--text-primary);
`;

const ActionButton = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  font-family: var(--font-regular);
  font-size: 12px;
  color: var(--dark-blue);
  cursor: pointer;

  &:disabled {
    color: var(--text-secondary);
    cursor: default;
  }
`;

const PickerContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 10px 20px;
  gap: 10px;
  height: 100%;
  overflow: hidden;
`;

const SearchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SearchInput = styled.div`
  flex: 1;
`;

const StyledDropdownMenu = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid var(--line-gray);
  justify-content: center;
  align-items: center;
  background-color: white;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
`;

const MenuWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 22px;
  max-height: 160px;
  width: 100%;

  .ant-dropdown-menu {
    padding: 0;
    border-radius: 0;
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
    overflow-y: auto;

    .ant-dropdown-menu-item {
      font-size: 12px;
      padding: 0;
      border-radius: 0;
      border-bottom: 1px solid var(--line-gray);
    }
  }
`;

const CommunityItem = styled.div`
  display: flex;
  gap: 4px;
  padding: 5px 12px;
  cursor: pointer;

  .light {
    color: var(--text-secondary);
  }
`;

type Props = {
  companyId: number | null | undefined;
  holdingId: number | null | undefined;
  isAdminUser: boolean;
  selectedCommunities: UserManagementCommunityOption[];
  fallbackCompanyName?: string | null;
  onSelectedCommunitiesChange: (communities: UserManagementCommunityOption[]) => void;
};

const UserManagementCommunitiesSection = ({
  companyId,
  holdingId,
  isAdminUser,
  selectedCommunities,
  fallbackCompanyName,
  onSelectedCommunitiesChange,
}: Props) => {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search);

  useEffect(() => {
    setSearch('');
  }, [companyId, holdingId]);

  const {
    data: availableCommunities,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ['user-management-communities', debouncedSearch, companyId, holdingId, isAdminUser],
    queryFn: () =>
      getUserManagementAvailableCommunities({
        search: debouncedSearch,
        company_id: isAdminUser ? companyId : undefined,
        holding_id: isAdminUser ? holdingId : undefined,
        page_size: 200,
      }),
    select: (data: any): UserManagementCommunityOption[] =>
      (data?.results ?? data ?? []) as UserManagementCommunityOption[],
    enabled: true,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  const communitiesDropdownOptions: any[] = useMemo(() => {
    const selectedIds = new Set(selectedCommunities.map((community) => community.id));

    return (availableCommunities || [])
      .filter((community: UserManagementCommunityOption) => !selectedIds.has(community.id))
      .map((community: UserManagementCommunityOption) => ({
        key: community.id,
        label: (
          <CommunityItem
            onClick={() => {
              onSelectedCommunitiesChange([...selectedCommunities, community]);
            }}
            data-test-id={`user-management-community-option-${community.id}`}
          >
            <span>{community.comunity_name}</span>
            <span className={'light'}>·</span>
            <span className={'light'}>{community.company?.name || fallbackCompanyName || ''}</span>
          </CommunityItem>
        ),
      }));
  }, [availableCommunities, fallbackCompanyName, onSelectedCommunitiesChange, selectedCommunities]);

  const communitiesSearchPlaceholder =
    isAdminUser && !companyId && !holdingId ? 'Search all communities to add' : 'Search for communities to add';

  const handleResetSelection = () => {
    onSelectedCommunitiesChange([]);
  };

  return (
    <Container>
      <Header>
        <Title>Communities</Title>
        <ActionButton
          type="button"
          disabled={selectedCommunities.length === 0}
          onClick={handleResetSelection}
          data-test-id="user-management-communities-reset"
        >
          Reset selection
        </ActionButton>
      </Header>
      <PickerContainer>
        <SearchRow>
          <SearchInput>
            <Dropdown
              menu={{ items: communitiesDropdownOptions }}
              trigger={['click']}
              dropdownRender={(menu: any) => (
                <StyledDropdownMenu className={'mini-scroll'}>
                  {(isLoading || isFetching) && <LoaderRibbed expanded size={'mini'} />}
                  {!isLoading && !isFetching && communitiesDropdownOptions.length === 0 && <span>No results</span>}
                  {!isLoading && !isFetching && communitiesDropdownOptions.length > 0 && (
                    <MenuWrapper className={'mini-scroll'}>{menu}</MenuWrapper>
                  )}
                </StyledDropdownMenu>
              )}
            >
              <Input
                prefix={<SearchOutlined />}
                value={search}
                onChange={(event: any) => setSearch(event.target.value)}
                placeholder={communitiesSearchPlaceholder}
                data-test-id="user-management-communities-search-input"
              />
            </Dropdown>
          </SearchInput>
        </SearchRow>

        <UserManagementSelectedCommunities
          communities={selectedCommunities}
          fallbackCompanyName={fallbackCompanyName}
          onChange={onSelectedCommunitiesChange}
        />
      </PickerContainer>
    </Container>
  );
};

export default UserManagementCommunitiesSection;
